import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface FeaturedFrameSearchParams {
  page?: number;
  images?: number;
  sortby?: string;
  sortdir?: string;
  pricelt?: number;
  pricegt?: number;
  product_type?: string;
  frame_style?: string;
  type_of_frame?: string;
  color?: string;
  orientation?: string;
  discount?: string;
  space?: string;
  size?: string;
  searchtext?: string;
}

@Injectable({ providedIn: 'root' })
export class FeaturedFramesService {
  private siteApi = `${environment.newApiUrl}/api/site`;
  private searchBase = (environment as any).newSearchApiBase || (environment as any).searchApiBase;

  // The whole filter panel now comes from the NEW backend in ONE call, cached for
  // the session. Each getX() below slices this so the listing page is unchanged.
  private filters$?: Observable<any>;

  constructor(private http: HttpClient) {}

  private allFilters(): Observable<any> {
    if (!this.filters$) {
      this.filters$ = this.http
        .get<any>(`${this.siteApi}/frames/featured-filters`)
        .pipe(shareReplay(1));
    }
    return this.filters$;
  }

  /** Slice one list out of the shared response as the old `{ data: [...] }` shape. */
  private filterList(key: string): Observable<any> {
    return this.allFilters().pipe(map((res: any) => ({ data: res?.data?.[key] || [] })));
  }

  // Solr search for featured frames
  searchFeaturedFrames(filters: FeaturedFrameSearchParams = {}): Observable<any> {
    let params = new HttpParams()
      .set('page', String(filters.page || 1))
      .set('images', String(filters.images || 24))
      .set('sortby', filters.sortby || 'popularity')
      .set('sortdir', filters.sortdir || 'asc')
      .set('product', 'frame_your_photos');

    if (filters.pricelt) params = params.set('pricelt', String(filters.pricelt));
    if (filters.pricegt) params = params.set('pricegt', String(filters.pricegt));
    if (filters.product_type) params = params.set('product_type', filters.product_type);
    if (filters.frame_style) params = params.set('frame_style', filters.frame_style);
    if (filters.type_of_frame) params = params.set('type_of_frame', filters.type_of_frame);
    if (filters.color) params = params.set('color', filters.color);
    if (filters.orientation) params = params.set('orientation', filters.orientation);
    if (filters.discount) params = params.set('discount', filters.discount);
    if (filters.space) params = params.set('space', filters.space);
    if (filters.size) params = params.set('size', filters.size);
    if (filters.searchtext) params = params.set('searchtext', filters.searchtext);

    return this.http.get<any>(`${this.searchBase}/search_on_sale.php`, { params });
  }

  // Filter option loaders — all sliced from the single cached NEW-backend call.
  getOrientations(): Observable<any> { return this.filterList('orientations'); }
  getSizes(): Observable<any> { return this.filterList('sizes'); }
  getFrameStyles(): Observable<any> { return this.filterList('frame_styles'); }
  getCollections(): Observable<any> { return this.filterList('collections'); }
  getColors(): Observable<any> { return this.filterList('colors'); }
  getSpaces(): Observable<any> { return this.filterList('spaces'); }
  getDiscounts(): Observable<any> { return this.filterList('discounts'); }
}
